(() => {
  const API_REVISION = '2026-07-15';
  const POPUP_DELAY = 40000;
  const DISMISS_KEY = 'le_dynamic_signup_popup_dismissed';
  const SUCCESS_KEY = 'le_dynamic_signup_complete';
  const DISMISS_FOR = 7 * 24 * 60 * 60 * 1000;
  const ANALYTICS_RETRY_DELAY = 250;
  const ANALYTICS_MAX_ATTEMPTS = 40;

  const storageGet = (key) => {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  };

  const storageSet = (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      return false;
    }

    return true;
  };

  const message = (root, key) => root.dataset[key] || '';

  const setStatus = (root, text, state = '') => {
    const status = root.querySelector('[data-le-signup-status]');
    if (!status) return;
    status.textContent = text;
    status.dataset.state = state;
  };

  const analyticsPayload = (root, extra = {}) => ({
    form_type: root.dataset.mode,
    signup_language: root.dataset.language,
    signup_country: root.dataset.country,
    signup_currency: root.dataset.currency,
    ...extra
  });

  const publishAnalyticsEvent = async (eventName, payload, attempt = 0) => {
    const publish = window.Shopify?.analytics?.publish;

    if (typeof publish !== 'function') {
      if (attempt < ANALYTICS_MAX_ATTEMPTS) {
        window.setTimeout(
          () => publishAnalyticsEvent(eventName, payload, attempt + 1),
          ANALYTICS_RETRY_DELAY
        );
      } else {
        console.error(`Shopify Analytics unavailable for ${eventName}`);
      }
      return;
    }

    try {
      const published = await publish.call(window.Shopify.analytics, eventName, payload);

      if (!published && attempt < ANALYTICS_MAX_ATTEMPTS) {
        window.setTimeout(
          () => publishAnalyticsEvent(eventName, payload, attempt + 1),
          ANALYTICS_RETRY_DELAY
        );
      } else if (!published) {
        console.error(`Shopify Analytics rejected ${eventName}`);
      }
    } catch (error) {
      if (attempt < ANALYTICS_MAX_ATTEMPTS) {
        window.setTimeout(
          () => publishAnalyticsEvent(eventName, payload, attempt + 1),
          ANALYTICS_RETRY_DELAY
        );
      } else {
        console.error(`Shopify Analytics failed for ${eventName}`, error);
      }
    }
  };

  const trackFormView = (root) => {
    if (root.dataset.analyticsViewTracked === 'true') return;
    root.dataset.analyticsViewTracked = 'true';
    publishAnalyticsEvent('lisa_eleni:newsletter_form_view', analyticsPayload(root));
  };

  const signupProperties = (root, tier, minimumSpend) => ({
    Staffelrabatt: `${tier}%`,
    signup_language: root.dataset.language,
    signup_country: root.dataset.country,
    signup_currency: root.dataset.currency,
    signup_tier: Number(tier),
    signup_minimum_spend: Number(minimumSpend),
    signup_source: root.dataset.mode
  });

  const subscribe = async (root, email, profileProperties) => {
    const url = new URL('https://a.klaviyo.com/client/subscriptions');
    url.searchParams.set('company_id', root.dataset.companyId);

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: `Lisa Eleni dynamic ${root.dataset.mode} signup`,
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                properties: profileProperties,
                subscriptions: {
                  email: {
                    marketing: {
                      consent: 'SUBSCRIBED'
                    }
                  }
                }
              }
            }
          }
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: root.dataset.listId
            }
          }
        }
      }
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        accept: 'application/vnd.api+json',
        'content-type': 'application/vnd.api+json',
        revision: API_REVISION
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) throw new Error(`Klaviyo subscription failed (${response.status})`);
  };

  const identifySubscriber = async (email, properties) => {
    const identify = window.LisaEleniKlaviyo?.identify;

    if (typeof identify !== 'function') return false;

    try {
      return await identify(email, properties);
    } catch (error) {
      console.error('Klaviyo browser identification failed', error);
      return false;
    }
  };

  const closePopup = (root, remember = true) => {
    root.hidden = true;
    document.body.classList.remove('le-signup-popup-open');
    if (remember) storageSet(DISMISS_KEY, String(Date.now()));
  };

  document.querySelectorAll('[data-le-signup]').forEach((root) => {
    const form = root.querySelector('[data-le-signup-form]');
    if (!form) return;

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      setStatus(root, '');

      if (!form.reportValidity()) return;

      const selectedTier = form.querySelector('input[name="signup_tier"]:checked');
      const email = form.elements.email.value.trim();
      const submitButton = form.querySelector('button[type="submit"]');
      const properties = signupProperties(
        root,
        selectedTier.value,
        selectedTier.dataset.minimumSpend
      );
      submitButton.disabled = true;

      try {
        await subscribe(root, email, properties);
        await identifySubscriber(email, properties);
        publishAnalyticsEvent(
          'lisa_eleni:newsletter_form_submit',
          analyticsPayload(root, {
            signup_tier: Number(selectedTier.value),
            signup_minimum_spend: Number(selectedTier.dataset.minimumSpend)
          })
        );
        form.reset();
        setStatus(root, message(root, 'successMessage'), 'success');
        storageSet(SUCCESS_KEY, String(Date.now()));

        if (root.dataset.mode === 'popup') {
          window.setTimeout(() => closePopup(root, false), 1800);
        }
      } catch (error) {
        console.error(error);
        setStatus(root, message(root, 'errorMessage'), 'error');
      } finally {
        submitButton.disabled = false;
      }
    });

    if (root.dataset.mode === 'popup') {
      root.querySelectorAll('[data-le-signup-close]').forEach((button) => {
        button.addEventListener('click', () => closePopup(root));
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !root.hidden) closePopup(root);
      });

      const dismissedAt = Number(storageGet(DISMISS_KEY) || 0);
      const hasCompleted = Boolean(storageGet(SUCCESS_KEY));
      const canOpen = !hasCompleted && Date.now() - dismissedAt > DISMISS_FOR;

      if (canOpen && !window.Shopify?.designMode) {
        window.setTimeout(() => {
          root.hidden = false;
          document.body.classList.add('le-signup-popup-open');
          trackFormView(root);
          root.querySelector('input[type="radio"]')?.focus();
        }, POPUP_DELAY);
      }
    } else if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          trackFormView(root);
          observer.disconnect();
        },
        { threshold: 0.5 }
      );
      observer.observe(root);
    } else {
      trackFormView(root);
    }
  });
})();
